import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getProducts, addToCart, EshopProduct } from '../services/eshopApi';
import { Box, SimpleGrid, Heading, Text, Image, Badge, Button, VStack, HStack, useToast, Link as ChakraLink } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

const formatPrice = (cents: number, currency: string) => {
  const value = cents / 100;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(value);
};

const ShopHomePage: React.FC = () => {
  const toast = useToast();
  const { data, isLoading, isError } = useQuery<EshopProduct[]>(['eshop-products'], getProducts);

  const handleAddToCart = async (product: EshopProduct) => {
    try {
      const firstVariant = product.variants && product.variants.length > 0 ? product.variants[0] : undefined;
      await addToCart(product.id, firstVariant?.id, 1);
      toast({ status: 'success', title: 'Přidáno do košíku', description: product.name, duration: 2000 });
    } catch (e) {
      toast({ status: 'error', title: 'Chyba', description: 'Nepodařilo se přidat do košíku.' });
    }
  };

  if (isLoading) {
    return <Text>Načítání produktů…</Text>;
  }

  if (isError || !data) {
    return <Text>Došlo k chybě při načítání produktů.</Text>;
  }

  if (data.length === 0) {
    return (
      <Box>
        <Heading size="lg" mb={2}>E-shop bude brzy spuštěn</Heading>
        <Text color="gray.600">Zatím zde nejsou žádné produkty. Ověřte nastavení e-shopu v administraci.</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={4}>Produkty</Heading>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6}>
        {data.map((p) => (
          <Box key={p.id} borderWidth="1px" borderRadius="md" overflow="hidden" bg="white" _hover={{ boxShadow: 'md' }}>
            <Box position="relative" paddingTop="70%" overflow="hidden">
              <Image
                src={p.default_image_url || '/images/placeholder-clothing.jpg'}
                alt={p.name}
                position="absolute"
                top={0}
                left={0}
                w="100%"
                h="100%"
                objectFit="cover"
              />
            </Box>
            <VStack align="stretch" p={4} spacing={2}>
              <ChakraLink as={RouterLink} to={`/produkt/${p.slug}`} fontWeight="semibold" noOfLines={2}>
                {p.name}
              </ChakraLink>
              {p.short_description && (
                <Text fontSize="sm" color="gray.600" noOfLines={2}>{p.short_description}</Text>
              )}
              <HStack justify="space-between" mt={1}>
                <Badge colorScheme="blue">{formatPrice(p.price_cents, p.currency || 'CZK')}</Badge>
                <Button size="sm" colorScheme="blue" onClick={() => handleAddToCart(p)}>
                  Do košíku
                </Button>
              </HStack>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default ShopHomePage;
